<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  quote: { type: Object, required: true },
  // When false, only the price box renders (insurances are handled separately,
  // e.g. BookStep's interactive selection). Defaults to read-only insurance list.
  showInsurances: { type: Boolean, default: true },
});

const { t } = useI18n();

const charges = computed(() => props.quote.charges || []);
const taxes = computed(() => props.quote.taxes || []);
const insurances = computed(() => props.quote.insurances || []);

// The quote's booking currency (what the customer is charged in, e.g. CAD).
const bookingCurrency = computed(
  () =>
    props.quote.totalPrice?.bookingCurrency || props.quote.totalPrice?.currency
);

// Each Money carries its own explicit bookingCurrency code (authoritative), so
// prefer that per line, falling back to the quote-level booking currency and
// finally the line's own currency for older gateway responses.
const formatPrice = price => {
  if (!price) return '';
  const amount = price.inBookingCurrency ?? price.amount;
  const code = price.bookingCurrency || bookingCurrency.value || price.currency;
  return `${amount} ${code}`;
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-1.5 rounded-lg border border-n-weak p-3">
      <p class="m-0 text-xs font-medium uppercase text-n-slate-10">
        {{ t('CONVERSATION.CAR_BOOKING.BOOK.PRICE_BREAKDOWN') }}
      </p>
      <div
        v-if="quote.basePrice"
        class="flex items-center justify-between text-sm text-n-slate-11"
      >
        <span>{{ t('CONVERSATION.CAR_BOOKING.BOOK.BASE_PRICE') }}</span>
        <span>{{ formatPrice(quote.basePrice) }}</span>
      </div>
      <div
        v-for="charge in charges"
        :key="charge.code"
        class="flex items-center justify-between text-sm text-n-slate-11"
      >
        <span>{{ charge.description }}</span>
        <span>{{ formatPrice(charge.amount) }}</span>
      </div>
      <div
        v-for="tax in taxes"
        :key="tax.code"
        class="flex items-center justify-between text-sm text-n-slate-11"
      >
        <span>{{ t('CONVERSATION.CAR_BOOKING.BOOK.TAX') }}</span>
        <span>
          {{
            t('CONVERSATION.CAR_BOOKING.BOOK.TAX_RATE', {
              code: tax.code,
              rate: tax.rate,
            })
          }}
        </span>
      </div>
      <div
        v-if="quote.totalPrice"
        class="mt-1 flex items-center justify-between border-t border-n-weak pt-1.5 text-sm font-semibold text-n-slate-12"
      >
        <span>{{ t('CONVERSATION.CAR_BOOKING.BOOK.TOTAL') }}</span>
        <span>{{ formatPrice(quote.totalPrice) }}</span>
      </div>
    </div>

    <div
      v-if="showInsurances && insurances.length"
      class="flex flex-col gap-1.5"
    >
      <p class="m-0 text-xs font-medium uppercase text-n-slate-10">
        {{ t('CONVERSATION.CAR_BOOKING.BOOK.INSURANCE') }}
      </p>
      <div
        v-for="insurance in insurances"
        :key="insurance.code"
        class="flex items-center justify-between gap-3 rounded-lg border border-n-weak px-3 py-2"
      >
        <div class="flex min-w-0 flex-col gap-0.5">
          <span class="truncate text-sm text-n-slate-12">
            {{ insurance.name }}
          </span>
          <span v-if="insurance.excess" class="text-xs text-n-slate-11">
            {{
              t('CONVERSATION.CAR_BOOKING.BOOK.EXCESS', {
                amount: formatPrice(insurance.excess),
              })
            }}
          </span>
        </div>
        <span
          v-if="insurance.type === 'INCLUDED'"
          class="shrink-0 text-xs font-medium text-n-green-11"
        >
          {{ t('CONVERSATION.CAR_BOOKING.BOOK.INSURANCE_INCLUDED') }}
        </span>
        <span
          v-else-if="insurance.price"
          class="shrink-0 text-sm text-n-slate-11"
        >
          {{ formatPrice(insurance.price) }}
        </span>
      </div>
    </div>
  </div>
</template>
